import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { Menu, X } from 'lucide-react'
import { TestOpsCommandLogo } from '@ytest/testops-command'
import clsx from 'clsx'

const YTEST_URL = import.meta.env.VITE_YTEST_URL || 'https://ytest.ai'

type NavLink =
  | { id: string; label: string; external?: never }
  | { id: string; label: string; external: string }

const sections: NavLink[] = [
  { id: 'capabilities', label: 'Capabilities' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'how-it-works', label: 'How it works' },
  { id: 'about', label: 'About ytest.ai', external: `${YTEST_URL}/#about` },
  { id: 'contact', label: 'Contact' },
]

export function Navbar() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4 }}
      className={clsx(
        'fixed top-0 left-0 right-0 z-50 transition-colors duration-300',
        scrolled
          ? 'border-b border-white/5 bg-zinc-950/80 backdrop-blur-xl'
          : 'bg-transparent',
      )}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Brand */}
        <a href="/" className="flex items-center gap-2.5">
          <TestOpsCommandLogo size={28} />
          <span className="font-mono text-sm font-semibold tracking-tight text-white">
            TestOps Command
          </span>
        </a>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex">
          {sections.map((s) => (
            <a
              key={s.id}
              href={s.external ?? `#${s.id}`}
              className="rounded-md px-3 py-1.5 text-sm text-white/70 transition hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
            >
              {s.label}
            </a>
          ))}
        </nav>

        {/* CTAs */}
        <div className="hidden items-center gap-2 md:flex">
          <a
            href={`${YTEST_URL}/login`}
            className="rounded-md px-3 py-1.5 text-sm text-white/80 transition hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
          >
            Sign in
          </a>
          <a
            href={`${YTEST_URL}/signup`}
            className="inline-flex items-center rounded-md bg-cyan-500 px-4 py-1.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/25 transition hover:bg-cyan-600 hover:shadow-cyan-500/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
          >
            Get started
          </a>
        </div>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          className="rounded-md p-2 text-white/80 hover:bg-white/5 md:hidden"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-white/5 bg-zinc-950/95 backdrop-blur md:hidden">
          <div className="mx-auto max-w-7xl space-y-1 px-4 py-4">
            {sections.map((s) => (
              <a
                key={s.id}
                href={s.external ?? `#${s.id}`}
                onClick={() => setMobileOpen(false)}
                className="block rounded-md px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white"
              >
                {s.label}
              </a>
            ))}
            <div className="border-t border-white/5 pt-3">
              <a
                href={`${YTEST_URL}/login`}
                className="block rounded-md px-3 py-2 text-sm text-white/70 hover:bg-white/5 hover:text-white"
              >
                Sign in
              </a>
              <a
                href={`${YTEST_URL}/signup`}
                className="mt-2 block rounded-md bg-cyan-500 px-3 py-2 text-center text-sm font-semibold text-white hover:bg-cyan-600"
              >
                Get started
              </a>
            </div>
          </div>
        </div>
      )}
    </motion.header>
  )
}
